(function () {
  const EASE = "cubic-bezier(0.65, 0, 0.35, 1)";
  const DUR = 1100;
  const CONTENT_DUR = 800;
  const CONTENT_OUT_DUR = 600;
  const CONTENT_STAGGER = [100, 200, 300]; // per-child enter delay (ms)
  const CARD_DUR = 600;
  const LEAVING_CLASS = "is-leaving";
  const MASK_CLASS = "featured-products__mask";

  // Real indices, not translate: a loop wrap jumps the translate, which would
  // read as a direction flip and wipe the slide in from the wrong side. With
  // exactly two slides both steps look alike, so the drag/click intent decides.
  const slideDirection = (swiper, intent) => {
    const fallback = intent || "next";
    const count = swiper.slides.length;
    const from = swiper.previousRealIndex;
    const to = swiper.realIndex;
    if (count < 2 || typeof from !== "number" || from === to) return fallback;
    const isNext = to === (from + 1) % count;
    const isPrev = to === (from - 1 + count) % count;
    if (isNext && isPrev) return fallback;
    if (isNext) return "next";
    if (isPrev) return "prev";
    return to > from ? "next" : "prev";
  };

  const setCurtain = (slide, percent, transition) => {
    const reveal = slide.querySelector(".featured-products__reveal");
    const inner = slide.querySelector(".featured-products__reveal-inner");
    if (!reveal || !inner) return;
    reveal.style.transition = transition;
    inner.style.transition = transition;
    reveal.style.transform = `translateX(${percent}%)`;
    inner.style.transform = `translateX(${-percent}%)`;
  };

  const endWrapperTransition = (swiper) => {
    const wrapper = swiper.wrapperEl;
    if (!wrapper) return;
    const event =
      typeof TransitionEvent === "function"
        ? new TransitionEvent("transitionend", { propertyName: "transform" })
        : new Event("transitionend");
    wrapper.dispatchEvent(event);
  };

  // Controller-linked tracks get their translate synced but no slide events, so
  // every track is matched to the driver by its real (unlooped) index.
  const slideByRealIndex = (swiper, realIndex) => {
    const match = swiper.slides.find(
      (slide) => Number(slide.dataset.swiperSlideIndex) === realIndex
    );
    return (
      match || swiper.slides[realIndex] || swiper.slides[swiper.activeIndex]
    );
  };

  // Parked slides stay in the DOM, so only the shown one keeps its links in the
  // tab order.
  const syncInert = (swiper, active = swiper.slides[swiper.activeIndex]) => {
    if (swiper.destroyed) return;
    swiper.slides.forEach((slide) => {
      slide.inert = slide !== active;
    });
  };

  const settleSlides = (swiper, keepNeighboursWarm) => {
    const total = swiper.slides.length;
    if (!total) return;

    const active = swiper.slides[swiper.activeIndex];
    const warm = keepNeighboursWarm
      ? new Set([
          swiper.slides[(swiper.activeIndex + 1) % total],
          swiper.slides[(swiper.activeIndex - 1 + total) % total],
        ])
      : new Set();

    swiper.slides.forEach((slide) => {
      if (slide === active) return;
      slide.classList.remove(LEAVING_CLASS);
      setCurtain(slide, warm.has(slide) ? 0 : 100, "none");
    });
    if (active) setCurtain(active, 0, "none");
  };

  const wipeSlide = (swiper, active, previous, direction) => {
    const dur = swiper.params.speed || DUR;
    const sign = direction === "prev" ? -1 : 1;
    const transition = `transform ${dur}ms ${EASE}`;

    const leaving = previous && previous !== active ? previous : null;

    swiper.slides.forEach((slide) => {
      if (slide === active || slide === leaving) return;
      slide.classList.remove(LEAVING_CLASS);
      setCurtain(slide, 100, "none");
    });

    if (active) {
      active.classList.remove(LEAVING_CLASS);
      setCurtain(active, sign * 100, "none");

      void active.offsetWidth;

      setCurtain(active, 0, transition);
    }

    if (leaving) {
      leaving.classList.add(LEAVING_CLASS);
      setCurtain(leaving, 0, "none");
      void leaving.offsetWidth;
      setCurtain(leaving, -sign * 100, transition);
    }
  };

  // Overlay content is a vertical stack, so each block gets its own clip box and
  // rides in on a stagger. Idempotent + reversible for theme-editor re-inits.
  const wrapContentMasks = (root) => {
    root
      .querySelectorAll(".featured-products__content--overlay")
      .forEach((content) => {
        Array.from(content.children).forEach((child) => {
          if (child.classList.contains(MASK_CLASS)) return;
          const mask = document.createElement("div");
          mask.className = MASK_CLASS;
          content.insertBefore(mask, child);
          mask.appendChild(child);
        });
      });
  };

  const unwrapContentMasks = (root) => {
    root
      .querySelectorAll(".featured-products__content ." + MASK_CLASS)
      .forEach((mask) => {
        const child = mask.firstElementChild;
        if (child) {
          mask.parentNode.insertBefore(child, mask);
          child.style.transition = "";
          child.style.transform = "";
          child.style.transitionDelay = "";
        }
        mask.remove();
      });
  };

  const animateContent = (slide, mode) => {
    if (!slide) return;
    slide.querySelectorAll("." + MASK_CLASS + " > *").forEach((el, i) => {
      if (mode === "out") {
        el.style.transition = `transform ${CONTENT_OUT_DUR}ms ${EASE}`;
        el.style.transitionDelay = "0ms";
        el.style.transform = "translate3d(0, -100%, 0)";
      } else {
        el.style.transition = "none";
        el.style.transform = "translate3d(0, 100%, 0)";
        void el.offsetWidth;
        const delay = CONTENT_STAGGER[Math.min(i, CONTENT_STAGGER.length - 1)];
        el.style.transition = `transform ${CONTENT_DUR}ms ${EASE}`;
        el.style.transitionDelay = delay + "ms";
        el.style.transform = "translate3d(0, 0, 0)";
      }
    });
  };

  // Re-sweep the tertiary button underline on the active slide.
  const resweepUnderline = (slide, active) => {
    if (!slide) return;
    slide.querySelectorAll(".button--tertiary").forEach((btn) => {
      btn.classList.toggle("is-underline-active", active === true);
    });
  };

  const resetCreativeStyles = (root) => {
    root.querySelectorAll(".swiper-slide").forEach((slide) => {
      slide.style.transition = "";
      slide.classList.remove(LEAVING_CLASS);
      slide.inert = false;
    });
    root
      .querySelectorAll(
        ".featured-products__reveal, .featured-products__reveal-inner"
      )
      .forEach((el) => {
        el.style.transition = "";
        el.style.transform = "";
      });
  };

  const SWIPE_MIN = 40;

  const releaseSwipeArea = (root) => {
    const area = root._fpSwipeArea;
    if (!area) return;
    area.removeEventListener("pointerdown", root._fpSwipeDown);
    area.removeEventListener("pointerup", root._fpSwipeEnd);
    area.removeEventListener("pointercancel", root._fpSwipeEnd);
    area.removeEventListener("click", root._fpSwipeClick, true);
    root._fpSwipeArea = null;
    root._fpSwipeDown = null;
    root._fpSwipeEnd = null;
    root._fpSwipeClick = null;
  };

  // A controlled transition leaves a transitionend listener on the follower's
  // wrapper that calls into the follower until it fires. The DOM is reused on
  // an editor reload, so the driver fires it while the follower still lives.
  const destroySwiper = (el) => {
    const swiper = el?.swiper;
    if (!swiper) return;
    clearTimeout(swiper._fpEndTimer);
    const follower = swiper.controller?.control;
    if (follower) {
      swiper.controller.control = null;
      if (!follower.destroyed) endWrapperTransition(follower);
    }
    swiper.destroy(true, true);
  };

  const endTransitionLater = (swiper, keepNeighboursWarm) => {
    clearTimeout(swiper._fpEndTimer);
    swiper._fpEndTimer = setTimeout(() => {
      if (swiper.destroyed) return;
      swiper.transitionEnd();
      endWrapperTransition(swiper);
      settleSlides(swiper, keepNeighboursWarm);
    }, swiper.params.speed + 50);
  };

  const trackOptions = (loop) => ({
    effect: "fade",
    fadeEffect: { crossFade: true },
    speed: DUR,
    slidesPerView: 1,
    spaceBetween: 0,
    rewind: loop,
    watchSlideProgress: true,
    resistanceRatio: 0,
  });

  const initSection = (section) => {
    if (!section || !section.classList.contains("featured-products-section")) {
      return;
    }

    const root = section.querySelector(".featured-products");
    if (!root) return;

    const mediaEl = root.querySelector(".js-featured-products-media");
    const cardsEl = root.querySelector(".js-featured-products-cards");
    const contentEl = root.querySelector(".js-featured-products-content");
    if (!mediaEl || !cardsEl || !contentEl) return;

    if (root._fpObserver) {
      root._fpObserver.disconnect();
      root._fpObserver = null;
    }
    if (root._fpHold) {
      root._fpHold.destroy();
      root._fpHold = null;
    }
    releaseSwipeArea(root);
    destroySwiper(mediaEl);
    destroySwiper(cardsEl);
    destroySwiper(contentEl);
    unwrapContentMasks(root);
    resetCreativeStyles(root);

    const isOverlay = root.classList.contains("featured-products--overlay");
    if (isOverlay) wrapContentMasks(root);

    const prevBtn = root.querySelector(".slider-nav-button--prev");
    const nextBtn = root.querySelector(".slider-nav-button--next");
    const paginationEl = root.querySelector(".slider-pagination");

    const hasNavigation = root.getAttribute("data-navigation") === "true";
    const hasPagination = root.getAttribute("data-pagination") === "true";
    const sliderLoop = root.getAttribute("data-loop") === "true";
    const hasAutoplay = root.getAttribute("data-autoplay") === "true";
    const pauseOnHover =
      root.getAttribute("data-autoplay-pause-on-hover") === "true";
    const autoplayDelay = Number(root.dataset.autoplayDelay || 5000);

    const tracks = [];

    // Which arrow was pressed, for the two-slide case the indices can't tell
    // apart. Captured so it lands before Swiper acts on the click.
    root.removeEventListener("click", root._fpNavIntent, true);
    root._fpNavIntent = (event) => {
      if (event.target.closest(".slider-nav-button--prev")) {
        root._fpIntent = "prev";
      } else if (event.target.closest(".slider-nav-button--next")) {
        root._fpIntent = "next";
      }
    };
    root.addEventListener("click", root._fpNavIntent, true);

    const revealTracks = (driver) => {
      const intent = driver.swipeDirection || root._fpIntent;
      const direction = slideDirection(driver, intent);
      root._fpIntent = null;

      stepCards(driver.realIndex, direction);

      tracks.forEach(({ swiper, isContent, isMedia }) => {
        if (swiper.destroyed) return;
        const active = slideByRealIndex(swiper, driver.realIndex);
        let previous = swiper._fpPreviousSlide;
        if (previous === active) previous = null;

        wipeSlide(swiper, active, previous, direction);

        if (isContent && isOverlay) {
          animateContent(previous, "out");
          animateContent(active, "in");
        }
        if (isContent) {
          resweepUnderline(previous, false);
          resweepUnderline(active, true);
          syncInert(swiper, active);
        }

        swiper._fpPreviousSlide = active;
        endTransitionLater(swiper, isMedia === true);
      });
    };

    // The card column slides horizontally (no wipe), so it runs on its own
    // shorter transition instead of being controller-linked to the media.
    const cardsSwiper = new Swiper(cardsEl, {
      speed: CARD_DUR,
      slidesPerView: 1,
      spaceBetween: 0,
      loop: sliderLoop,
      allowTouchMove: false,
      a11y: { scrollOnFocus: false },
    });
    cardsSwiper.on("slideChange", () => syncInert(cardsSwiper));

    // A neighbouring target is stepped, never addressed by index: on a loop wrap
    // the absolute index sits at the far end of the track and would sweep the
    // whole row instead of sliding one card across.
    const stepCards = (realIndex, direction) => {
      if (cardsSwiper.destroyed) return;
      const count = cardsSwiper.slides.length;
      const from = cardsSwiper.realIndex;
      if (!count || from === realIndex) return;

      const isNeighbour =
        realIndex === (from + 1) % count ||
        realIndex === (from - 1 + count) % count;

      if (isNeighbour && !cardsSwiper.animating) {
        if (direction === "prev") {
          cardsSwiper.slidePrev(CARD_DUR);
        } else {
          cardsSwiper.slideNext(CARD_DUR);
        }
        return;
      }

      if (sliderLoop) {
        cardsSwiper.slideToLoop(realIndex, CARD_DUR);
      } else {
        cardsSwiper.slideTo(realIndex, CARD_DUR);
      }
    };

    // Followers: the media track is the only one that takes input and the only
    // one that reveals. Two drivers used to fire the change twice — the second
    // pass restarted the wipe from the parked state and the indices drifted.
    const contentSwiper = new Swiper(contentEl, {
      ...trackOptions(sliderLoop),
      allowTouchMove: false,
      a11y: { scrollOnFocus: false },
    });

    const mediaSwiper = new Swiper(mediaEl, {
      ...trackOptions(sliderLoop),
      allowTouchMove: true,
      mousewheel: { forceToAxis: true },
      autoplay: hasAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
          }
        : false,
      pagination: hasPagination ? { el: paginationEl, clickable: true } : false,
      navigation: hasNavigation
        ? {
            nextEl: nextBtn,
            prevEl: prevBtn,
            disabledClass: "swiper-button-disabled",
          }
        : false,
      on: {
        slideChangeTransitionStart() {
          revealTracks(this);
        },
        touchEnd(swiper) {
          if (!sliderLoop || swiper.destroyed) return;

          const direction = swiper.swipeDirection;
          if (!direction) return;
          if (Math.abs(swiper.touches.diff) < SWIPE_MIN) return;

          const atEdge =
            direction === "next" ? swiper.isEnd : swiper.isBeginning;
          if (!atEdge) return;

          requestAnimationFrame(() => {
            if (swiper.destroyed) return;
            if (direction === "next") swiper.slideNext();
            else swiper.slidePrev();
          });
        },
        autoplayTimeLeft(s, time, progress) {
          if (hasPagination && paginationEl) {
            paginationEl.style.setProperty("--slider-progress", 1 - progress);
          }
        },
      },
    });

    // One-way control, so the follower indices can never drift from the driver.
    mediaSwiper.controller.control = contentSwiper;

    const settleCards = () => {
      if (cardsSwiper.destroyed || mediaSwiper.destroyed) return;
      if (cardsSwiper.animating || mediaSwiper.animating) return;

      const target = mediaSwiper.realIndex;
      if (cardsSwiper.realIndex === target) return;

      if (sliderLoop) cardsSwiper.slideToLoop(target, CARD_DUR);
      else cardsSwiper.slideTo(target, CARD_DUR);
    };

    cardsSwiper.on("transitionEnd", settleCards);
    mediaSwiper.on("transitionEnd", settleCards);

    if (root._fpWheelWrap) {
      mediaEl.removeEventListener("wheel", root._fpWheelWrap);
    }
    root._fpWheelWrap = (event) => {
      if (!sliderLoop || mediaSwiper.destroyed || mediaSwiper.animating) return;

      const delta = event.deltaX;
      if (!delta) return;

      const forward = mediaSwiper.rtlTranslate ? delta < 0 : delta > 0;
      const atEdge = forward ? mediaSwiper.isEnd : mediaSwiper.isBeginning;
      if (!atEdge) return;

      event.preventDefault();
      if (forward) mediaSwiper.slideNext();
      else mediaSwiper.slidePrev();
    };
    mediaEl.addEventListener("wheel", root._fpWheelWrap, { passive: false });

    const swipeArea =
      root.querySelector(".featured-products__cards-col") || cardsEl;
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swiping = false;
    let swiped = false;

    root._fpSwipeArea = swipeArea;
    root._fpSwipeDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      swiping = true;
      swiped = false;
      swipeStartX = event.clientX;
      swipeStartY = event.clientY;
    };
    root._fpSwipeEnd = (event) => {
      if (!swiping) return;
      swiping = false;
      const dx = event.clientX - swipeStartX;
      const dy = event.clientY - swipeStartY;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
      swiped = true;
      if (mediaSwiper.destroyed) return;
      const isRtl = document.documentElement.dir === "rtl";
      const forward = dx < 0 !== isRtl;
      if (forward) mediaSwiper.slideNext();
      else mediaSwiper.slidePrev();
    };
    root._fpSwipeClick = (event) => {
      if (!swiped) return;
      swiped = false;
      event.preventDefault();
      event.stopPropagation();
    };
    swipeArea.addEventListener("pointerdown", root._fpSwipeDown);
    swipeArea.addEventListener("pointerup", root._fpSwipeEnd);
    swipeArea.addEventListener("pointercancel", root._fpSwipeEnd);
    swipeArea.addEventListener("click", root._fpSwipeClick, true);

    tracks.push(
      { swiper: mediaSwiper, isMedia: true },
      { swiper: contentSwiper, isContent: true }
    );

    tracks.forEach(({ swiper, isMedia }) => {
      swiper._fpPreviousSlide = swiper.slides[swiper.activeIndex];
      settleSlides(swiper, isMedia === true);
    });
    syncInert(cardsSwiper);
    syncInert(contentSwiper);

    // Draw the first slide's underline in on paint, like the slideshow does.
    requestAnimationFrame(() => {
      if (contentSwiper.destroyed) return;
      resweepUnderline(contentSwiper.slides[contentSwiper.activeIndex], true);
    });

    if (hasAutoplay && mediaSwiper.autoplay) {
      root._fpHold = holdSwiperAutoplay(mediaSwiper, root, {
        hoverTarget: pauseOnHover ? root : null,
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          root._fpHold?.set("offscreen", !entry.isIntersecting);
        });
      });
      observer.observe(section);
      root._fpObserver = observer;
    }
  };

  initSection(document.currentScript.parentElement);

  if (!window.__themeFeaturedProductsEvents) {
    window.__themeFeaturedProductsEvents = true;

    document.addEventListener("shopify:section:load", function (event) {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      if (typeof event.target?.querySelector !== "function") return;
      const root = event.target.querySelector(".featured-products");
      if (!root) return;
      if (root._fpObserver) {
        root._fpObserver.disconnect();
        root._fpObserver = null;
      }
      if (root._fpNavIntent) {
        root.removeEventListener("click", root._fpNavIntent, true);
        root._fpNavIntent = null;
      }
      if (root._fpHold) {
        root._fpHold.destroy();
        root._fpHold = null;
      }
      releaseSwipeArea(root);
      if (root._fpWheelWrap) {
        root
          .querySelector(".js-featured-products-media")
          ?.removeEventListener("wheel", root._fpWheelWrap);
        root._fpWheelWrap = null;
      }
      destroySwiper(root.querySelector(".js-featured-products-media"));
      destroySwiper(root.querySelector(".js-featured-products-cards"));
      destroySwiper(root.querySelector(".js-featured-products-content"));
      unwrapContentMasks(root);
      resetCreativeStyles(root);
    });
  }
})();
